import { parseExpression } from 'cron-parser';

// Define the 6 possible field types for clarity
export type CronField =
  | 'second'
  | 'minute'
  | 'hour'
  | 'dayOfMonth'
  | 'month'
  | 'dayOfWeek';

// A map to find the correct index for each field
const fieldIndexes: Record<CronField, number> = {
  second: 0,
  minute: 1,
  hour: 2,
  dayOfMonth: 3,
  month: 4,
  dayOfWeek: 5,
};

/**
 * Checks if a single cron field expression is valid by testing it
 * within a complete (but inert) cron string.
 *
 * @param fieldExpression The single part to test (e.g., "*[slash]2", "10-30", "5")
 * @param fieldType The type of field this expression is for (e.g., "minute")
 * @returns true if the part is valid, false if it's not.
 */
export function isCronFieldValid(
  fieldExpression: string,
  fieldType: CronField,
): boolean {
  // 1. Create a known-valid "base" expression (all wildcards)
  const baseCronParts = ['*', '*', '*', '*', '*', '*'];

  // 2. Find the correct index for the field we're testing
  const indexToTest = fieldIndexes[fieldType];

  // 3. Substitute the user's input into that position
  baseCronParts[indexToTest] = fieldExpression;

  // 4. Re-join the parts into a full test string
  const testCronString = baseCronParts.join(' ');

  try {
    // 5. Attempt to parse the full string.
    // If this succeeds, the part is valid!
    parseExpression(testCronString);
    return true;
  } catch (_err) {
    // 6. If parseExpression throws an error, the part is invalid.
    return false;
  }
}
